import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { PageHeader, Card, Button, Empty, Input, Select, Label, Textarea } from "@/components/Primitives";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Trash2, Pencil, Repeat, LayoutGrid, Rows3 } from "lucide-react";
import { toast } from "sonner";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function statusVariant(s) {
  if (s === "completed") return "success";
  if (s === "in_progress") return "primary";
  if (s === "review") return "warning";
  return "default";
}

const COLS = [
  { id: "pending", label: "Pending", dot: "bg-[hsl(var(--warning))]" },
  { id: "submitted", label: "Submitted", dot: "bg-[#8B5CF6]" },
  { id: "in_progress", label: "In progress", dot: "bg-[hsl(var(--primary))]" },
  { id: "completed", label: "Completed", dot: "bg-[hsl(var(--success))]" },
  { id: "cancelled", label: "Cancelled", dot: "bg-[hsl(var(--danger))]" },
];

export default function ProjectsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [wos, setWos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editingProj, setEditingProj] = useState(null);
  const [dragWoId, setDragWoId] = useState(null);
  const [team, setTeam] = useState([]);
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem("projects_density") || "comfortable"; } catch { return "comfortable"; }
  });
  const isAdmin = user?.role !== "customer";

  useEffect(() => {
    try { localStorage.setItem("projects_density", density); } catch {}
  }, [density]);

  const load = async () => {
    const [p, w, c, t] = await Promise.all([
      api.get("/projects"),
      api.get("/work-orders"),
      isAdmin ? api.get("/customers") : Promise.resolve({ data: [] }),
      isAdmin ? api.get("/team") : Promise.resolve({ data: [] }),
    ]);
    setProjects(p.data || []);
    setWos(w.data || []);
    setCustomers(c.data || []);
    setTeam(t.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const moveWO = async (id, status) => {
    await api.patch(`/work-orders/${id}`, { status });
    const updated = wos.map((w) => (w.id === id ? { ...w, status } : w));
    setWos(updated);
  };

  const deleteWO = async (w) => {
    if (!window.confirm(`Delete work order "${w.title}"?`)) return;
    await api.delete(`/work-orders/${w.id}`);
    toast.success("Work order deleted");
    load();
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`Delete project "${p.name}"? This does not delete related work orders.`)) return;
    await api.delete(`/projects/${p.id}`);
    toast.success("Project deleted");
    load();
  };

  const onDragStart = (id) => setDragWoId(id);
  const onDrop = (status) => {
    if (!dragWoId) return;
    moveWO(dragWoId, status);
    setDragWoId(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Delivery · Projects"
        title="Projects & work orders"
        subtitle="Deliverables, priorities, and progress across every customer."
        actions={
          <>
            <div className="hidden md:inline-flex items-center gap-0.5 p-0.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))]" data-testid="projects-density-toggle">
              <button
                type="button"
                onClick={() => setDensity("comfortable")}
                className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md text-[12px] font-semibold transition-colors ${density === "comfortable" ? "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]" : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"}`}
                title="Comfortable view"
                data-testid="density-comfortable"
              >
                <LayoutGrid className="w-3.5 h-3.5" strokeWidth={1.8}/>Comfortable
              </button>
              <button
                type="button"
                onClick={() => setDensity("compact")}
                className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md text-[12px] font-semibold transition-colors ${density === "compact" ? "bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))]" : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"}`}
                title="Compact view. Fits more projects on screen"
                data-testid="density-compact"
              >
                <Rows3 className="w-3.5 h-3.5" strokeWidth={1.8}/>Compact
              </button>
            </div>
            {isAdmin && <Button onClick={() => setEditingProj("new")} data-testid="new-project-btn"><Plus className="w-4 h-4" strokeWidth={2}/>New project</Button>}
          </>
        }
      />

      <div
        className={
          density === "compact"
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
            : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        }
        data-testid="projects-grid"
      >
        {projects.map((p) => {
          const billing = p.billing_type || "fixed";
          const isFixed = billing === "fixed" || billing === "hybrid";
          const isHybrid = billing === "hybrid";
          const budget = parseFloat(p.budget || 0);
          const monthlyFee = parseFloat(p.monthly_fee || 0);
          return (
            <div
              key={p.id}
              onClick={() => nav(`/projects/${p.id}`)}
              className={`rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] ${density === "compact" ? "p-3.5" : "p-5"} hover:-translate-y-[1px] transition-all group cursor-pointer`}
              data-testid="project-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] inline-flex items-center gap-2">
                    {p.customer_name || "Internal"}
                    {!p.customer_id && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]" data-testid="internal-badge">Internal</span>}
                  </div>
                  <div className="text-[16px] font-semibold mt-0.5 leading-tight">{p.name}</div>
                </div>
                <StatusPill variant={statusVariant(p.status)}>{(p.status || "").replace("_", " ")}</StatusPill>
                {isAdmin && (
                  <div className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setEditingProj(p)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))]" title="Edit"><Pencil className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                    <button onClick={() => deleteProject(p)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600" title="Delete"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                  </div>
                )}
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-[11px] text-[hsl(var(--text-muted))] mb-1.5"><span>Progress</span><span className="font-mono">{p.progress || 0}%</span></div>
                <div className="h-1.5 bg-[hsl(var(--surface-hover))] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--primary-hover))] rounded-full transition-all" style={{ width: `${p.progress || 0}%` }} />
                </div>
              </div>
              {p.financials && isFixed && p.financials.budget > 0 && (
                <div className="mt-3" data-testid="project-financials">
                  <div className="flex items-center justify-between text-[11px] text-[hsl(var(--text-muted))] mb-1.5">
                    <span>Payment</span>
                    <span className="font-mono text-[11px]">
                      <span className="text-[hsl(var(--success))] font-semibold">{gbp(p.financials.paid)}</span>
                      <span className="text-[hsl(var(--text-subtle))]"> / </span>
                      <span>{gbp(isFixed && p.financials.budget > 0 ? p.financials.budget : p.financials.invoiced)}</span>
                      <span className="ml-1.5 text-[hsl(var(--text-muted))]">· {p.financials.payment_progress}%</span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-[hsl(var(--surface-hover))] rounded-full overflow-hidden relative">
                    <div className="h-full bg-gradient-to-r from-[hsl(var(--success))] to-[hsl(var(--success))]/80 rounded-full transition-all" style={{ width: `${Math.min(p.financials.payment_progress || 0, 100)}%` }} />
                    {p.financials.outstanding > 0 && isFixed && p.financials.budget > 0 && (
                      <div className="absolute top-0 h-full bg-amber-500/40" style={{ left: `${Math.min(p.financials.payment_progress || 0, 100)}%`, width: `${Math.min((p.financials.outstanding / p.financials.budget) * 100, 100 - (p.financials.payment_progress || 0))}%` }}/>
                    )}
                  </div>
                  {p.financials.outstanding > 0 && (
                    <div className="mt-1 text-[10px] text-amber-600 dark:text-amber-400 uppercase tracking-widest font-semibold">{gbp(p.financials.outstanding)} outstanding</div>
                  )}
                </div>
              )}
              <div className="mt-3 flex items-center justify-between text-[11px] text-[hsl(var(--text-muted))]">
                <span>{p.start_date ? new Date(p.start_date).toLocaleDateString() : "—"} → {p.end_date ? new Date(p.end_date).toLocaleDateString() : "—"}</span>
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  {budget > 0 && (
                    <span className="text-[hsl(var(--primary))]">{gbp(budget)} {isHybrid ? "setup" : "budget"}</span>
                  )}
                  {monthlyFee > 0 && (billing === "ongoing" || isHybrid) && (
                    <span className="inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400" data-testid="project-monthly-chip">
                      <Repeat className="w-3 h-3" strokeWidth={2}/>{gbp(monthlyFee)}/mo
                    </span>
                  )}
                  {!budget && !monthlyFee && (
                    <span className="text-amber-600 dark:text-amber-400">{billing === "ongoing" ? "Ongoing" : "—"}</span>
                  )}
                </span>
              </div>
              {(p.assignee_ids || []).length > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {(p.assignee_ids || []).slice(0, 5).map((aid) => {
                      const m = team.find((x) => x.id === aid);
                      if (!m) return null;
                      return (
                        <img
                          key={aid}
                          src={m.avatar_url || "/brand/soubor-one-mark.png"}
                          onError={(e) => { e.currentTarget.src = "/brand/soubor-one-mark.png"; }}
                          title={m.name}
                          alt=""
                          className="w-6 h-6 rounded-full object-cover ring-2 ring-[hsl(var(--surface))]"
                        />
                      );
                    })}
                  </div>
                  {p.assignee_ids.length > 5 && (
                    <span className="text-[11px] text-[hsl(var(--text-muted))] font-mono">+{p.assignee_ids.length - 5}</span>
                  )}
                </div>
              )}
              {(p.assignee_ids || []).length === 0 && (p.team || []).length > 0 && (
                <div className="mt-3 text-[11px] text-[hsl(var(--text-muted))]">
                  Team: {(p.team || []).join(" · ")}
                </div>
              )}
            </div>
          );
        })}
        {projects.length === 0 && <div className="md:col-span-2 xl:col-span-3"><Empty title="No projects" subtitle={isAdmin ? "Click 'New project' to create one." : ""}/></div>}
      </div>

      <Card title="Work orders · Kanban" right={<span className="text-[11px] text-[hsl(var(--text-muted))]">Drag cards to move</span>}>
        <div className="p-5 overflow-x-auto">
          <div className="grid grid-cols-5 gap-3 min-w-[1100px]">
            {COLS.map((c) => (
              <div
                key={c.id}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2", "ring-[hsl(var(--primary))]"); }}
                onDragLeave={(e) => e.currentTarget.classList.remove("ring-2", "ring-[hsl(var(--primary))]")}
                onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("ring-2", "ring-[hsl(var(--primary))]"); onDrop(c.id); }}
                className="rounded-xl bg-[hsl(var(--background))] border border-[hsl(var(--border))] p-3 min-h-[240px] transition-all"
                data-testid={`wo-column-${c.id}`}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                    <div className="text-[12px] font-semibold">{c.label}</div>
                  </div>
                  <div className="text-[10px] font-mono text-[hsl(var(--text-muted))] bg-[hsl(var(--surface))] rounded px-1.5 py-0.5">
                    {wos.filter((w) => w.status === c.id).length}
                  </div>
                </div>
                <div className="space-y-2">
                  {wos.filter((w) => w.status === c.id).map((w) => (
                    <div
                      key={w.id}
                      draggable={isAdmin}
                      onDragStart={() => onDragStart(w.id)}
                      onDragEnd={() => setDragWoId(null)}
                      className={`p-3 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] transition-all group ${isAdmin ? "cursor-grab active:cursor-grabbing" : ""}`}
                      data-testid="wo-card"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-[13px] font-medium leading-snug flex-1">{w.title}</div>
                        <StatusPill variant={w.priority === "Urgent" ? "danger" : w.priority === "High" ? "warning" : "default"} className="shrink-0">{w.priority}</StatusPill>
                      </div>
                      <div className="mt-2 text-[11px] text-[hsl(var(--text-muted))]">{w.customer_name}</div>
                      {isAdmin && (
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div className="flex gap-1 flex-wrap opacity-60 group-hover:opacity-100 transition-opacity">
                            {COLS.filter((cc) => cc.id !== c.id).slice(0, 2).map((cc) => (
                              <button key={cc.id} onClick={() => moveWO(w.id, cc.id)} className="text-[10px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] px-1.5 py-0.5 rounded border border-[hsl(var(--border))]">→ {cc.label}</button>
                            ))}
                          </div>
                          <button onClick={() => deleteWO(w)} className="opacity-60 hover:opacity-100 p-1 rounded hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600 transition-all" title="Delete"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {editingProj && (
        <ProjectModal
          initial={editingProj === "new" ? null : editingProj}
          customers={customers}
          onClose={() => setEditingProj(null)}
          onSaved={() => { setEditingProj(null); load(); }}
        />
      )}
    </div>
  );
}

function ProjectModal({ initial, customers, onClose, onSaved }) {
  const isNew = !initial;
  const [team, setTeam] = useState([]);
  useEffect(() => { api.get("/team").then(({ data }) => setTeam(data || [])); }, []);

  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    description: initial?.description || "",
    customer_id: initial?.customer_id || "",
    customer_name: initial?.customer_name || "",
    is_internal: initial ? !initial.customer_id : false,
    status: initial?.status || "in_progress",
    start_date: (initial?.start_date || "").split("T")[0],
    end_date: (initial?.end_date || "").split("T")[0],
    progress: initial?.progress ?? 0,
    project_type: initial?.project_type || "",
    project_cost: initial?.project_cost ?? 0,
    billing_type: initial?.billing_type || "fixed",
    budget: initial?.budget ?? 0,
    monthly_fee: initial?.monthly_fee ?? 0,
    assignee_ids: initial?.assignee_ids || [],
    team: (initial?.team || []).join(", "),
    cover_image_url: initial?.cover_image_url || "",
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleAssignee = (mid) => {
    const curr = form.assignee_ids || [];
    const next = curr.includes(mid) ? curr.filter((x) => x !== mid) : [...curr, mid];
    // Keep legacy `team` string in sync for display
    const names = team.filter((t) => next.includes(t.id)).map((t) => t.name.split(" ")[0]);
    setForm((f) => ({ ...f, assignee_ids: next, team: names.join(", ") }));
  };

  const setCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    setForm((f) => ({ ...f, customer_id: cid, customer_name: c?.company || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        customer_id: form.is_internal ? null : (form.customer_id || null),
        customer_name: form.is_internal ? null : (form.customer_name || null),
        progress: parseInt(form.progress) || 0,
        project_cost: parseFloat(form.project_cost) || 0,
        budget: parseFloat(form.budget) || 0,
        monthly_fee: parseFloat(form.monthly_fee) || 0,
        team: form.team.split(",").map((s) => s.trim()).filter(Boolean),
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      };
      delete payload.is_internal;
      if (!form.is_internal && !form.customer_id) {
        toast.error("Pick a customer. Or tick 'Internal project'");
        setSaving(false);
        return;
      }
      if (isNew) {
        await api.post("/projects", payload);
        toast.success("Project created");
      } else {
        await api.patch(`/projects/${initial.id}`, payload);
        toast.success("Project updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="project-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">{isNew ? "New project" : `Edit ${initial.name}`}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div><Label>Project name *</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="project-name"/></div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)}/></div>

          <div className="grid grid-cols-2 gap-4">
            <div><Label>Customer {form.is_internal ? "" : "*"}</Label>
              <Select disabled={form.is_internal} value={form.customer_id} onChange={(e) => setCustomer(e.target.value)} data-testid="project-customer">
                <option value="">{form.is_internal ? "(Internal project)" : "Select a customer…"}</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
              </Select>
              <label className="mt-2 inline-flex items-center gap-2 text-[12px] text-[hsl(var(--text-muted))] cursor-pointer select-none" data-testid="internal-project-toggle">
                <input type="checkbox" checked={form.is_internal} onChange={(e) => setForm((f) => ({ ...f, is_internal: e.target.checked, customer_id: e.target.checked ? "" : f.customer_id, customer_name: e.target.checked ? "" : f.customer_name }))} className="accent-[hsl(var(--primary))]"/>
                Internal project (no customer)
              </label>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="planning">Planning</option>
                <option value="in_progress">In progress</option>
                <option value="review">Review</option>
                <option value="completed">Completed</option>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-[hsl(var(--border))] p-4 bg-[hsl(var(--background))]">
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold mb-3">Billing</div>
            <div className="grid grid-cols-3 gap-3">
              <button type="button" onClick={() => set("billing_type", "fixed")} className={`rounded-lg border p-3 text-left transition-all ${form.billing_type === "fixed" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-muted))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`} data-testid="billing-fixed">
                <div className="font-semibold text-[13px]">Fixed price</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">One-off agreed budget.</div>
              </button>
              <button type="button" onClick={() => set("billing_type", "hybrid")} className={`rounded-lg border p-3 text-left transition-all ${form.billing_type === "hybrid" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-muted))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`} data-testid="billing-hybrid">
                <div className="font-semibold text-[13px]">Setup + Monthly</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">One-off + recurring fee.</div>
              </button>
              <button type="button" onClick={() => set("billing_type", "ongoing")} className={`rounded-lg border p-3 text-left transition-all ${form.billing_type === "ongoing" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-muted))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`} data-testid="billing-ongoing">
                <div className="font-semibold text-[13px]">Ongoing only</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-0.5">Retainer / rolling work.</div>
              </button>
            </div>
            {(form.billing_type === "fixed" || form.billing_type === "hybrid") && (
              <div className="mt-3">
                <Label>{form.billing_type === "hybrid" ? "Setup / one-off (£)" : "Agreed budget (£)"}</Label>
                <Input type="number" min="0" step="0.01" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="project-budget" placeholder="e.g. 5000"/>
              </div>
            )}
            {(form.billing_type === "ongoing" || form.billing_type === "hybrid") && (
              <div className="mt-3">
                <Label>Monthly fee (£/mo)</Label>
                <Input type="number" min="0" step="0.01" value={form.monthly_fee} onChange={(e) => set("monthly_fee", e.target.value)} data-testid="project-monthly-fee" placeholder="e.g. 200"/>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">Recurring revenue tracked separately from setup invoices.</div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)}/></div>
            <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)}/></div>
            <div><Label>Progress (%)</Label><Input type="number" min="0" max="100" value={form.progress} onChange={(e) => set("progress", e.target.value)}/></div>
            <div><Label>Project type</Label><Input value={form.project_type} onChange={(e) => set("project_type", e.target.value)} placeholder="Mobile app, Website…"/></div>
          </div>

          <div>
            <Label>Team members assigned</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {team.filter((m) => m.active !== false).map((m) => {
                const on = (form.assignee_ids || []).includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleAssignee(m.id)}
                    className={`text-[11px] px-2.5 py-1 rounded-md border font-semibold transition-all inline-flex items-center gap-1.5 ${on ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:border-[hsl(var(--primary))]"}`}
                    data-testid="project-assignee-chip"
                  >
                    {m.name}
                    {m.type === "external" && <span className="text-[9px] opacity-70">EXT</span>}
                  </button>
                );
              })}
              {team.length === 0 && <span className="text-[11px] text-[hsl(var(--text-subtle))]">No team members. Add them on /team first</span>}
            </div>
          </div>

          <div><Label>Cover image URL</Label><Input value={form.cover_image_url} onChange={(e) => set("cover_image_url", e.target.value)} placeholder="Optional hero image"/></div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="project-save-btn">{saving ? "Saving…" : isNew ? "Create project" : "Save"}</Button>
        </div>
      </form>
    </div>
  );
}
